import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Spinner,
    Form,
    Badge,
    Table
} from "react-bootstrap";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from "chart.js";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
    FaChartPie,
    FaChartLine,
    FaUserInjured,
    FaMoneyBillAlt,
    FaUsers
} from "react-icons/fa";
import {
    getFirestore,
    collection,
    query,
    where,
    collectionGroup
} from "firebase/firestore";
// getDocs instrumentado: informa cuántas lecturas hace esta pantalla para
// poder atribuir el consumo de Firestore a cada usuario (visible solo para superAdmin).
import { getDocs } from "../../functions/getDocsInstrumentado";
import { establecerOrigenConsumo } from "../../functions/consumoLecturas";
import firebaseApp from "../../firebase/credenciales";

// Registrar componentes de ChartJS
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

const db = getFirestore(firebaseApp);

function DashboardAusencias({ usuario }) {
    const [loading, setLoading] = useState(false);
    const [periodo, setPeriodo] = useState(new Date().getFullYear());
    const [kpis, setKpis] = useState({
        tasaAusentismo: 0,
        diasPerdidos: 0,
        costoEstimado: 0,
        empleadosAfectados: 0
    });
    const [chartData, setChartData] = useState({
        tendencia: null,
        distribucion: null,
        topEmpleados: []
    });

    useEffect(() => {
        cargarDatos();
    }, [usuario, periodo]);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            establecerOrigenConsumo("ausencias:dashboard");
            const ruc = usuario.ruc || usuario.rucEmpresa;

            // 1. Obtener empleados para calcular total de fuerza laboral
            const usersRef = collection(db, "users");
            const qUsers = query(
                usersRef,
                where("rucEmpresa", "==", ruc),
                where("activo", "==", true)
            );
            const usersSnap = await getDocs(qUsers);
            const totalEmpleados = usersSnap.size || 1; // Evitar división por cero
            const empleadosMap = {};
            usersSnap.forEach(doc => {
                empleadosMap[doc.data().dni] = doc.data().nombreCompleto || doc.data().nombre;
            });

            // 2. Obtener ausencias del año seleccionado
            const inicioAno = new Date(periodo, 0, 1);
            const finAno = new Date(periodo, 11, 31);

            // Usamos collectionGroup para traer todas las ausencias
            // Nota: Igual que en otros componentes, filtramos en cliente si es necesario
            // pero idealmente confiamos en el índice compuesto ruc + fecha
            const ausenciasRef = collectionGroup(db, "solicitudes_ausencia");
            const qAusencias = query(
                ausenciasRef,
                where("rucEmpresa", "==", ruc),
                where("fechaInicio", ">=", inicioAno),
                where("fechaInicio", "<=", finAno),
                where("estado", "==", "aprobado") // Solo aprobadas cuentan para KPI real
            );

            const snapAusencias = await getDocs(qAusencias);

            let totalDias = 0;
            let totalCosto = 0;
            const porMes = Array(12).fill(0);
            const porTipo = {};
            const porEmpleado = {};

            snapAusencias.forEach(doc => {
                const data = doc.data();
                const dias = data.totalDias || 0;
                const mes = data.fechaInicio.toDate().getMonth();
                const tipo = data.tipo;
                const dni = data.empleadoDni;

                totalDias += dias;
                porMes[mes] += dias;

                // Conteo por tipo
                porTipo[tipo] = (porTipo[tipo] || 0) + dias;

                // Conteo por empleado
                porEmpleado[dni] = (porEmpleado[dni] || 0) + dias;

                // Estimación de costo (Sueldo base promedio / 30 * dias)
                // Usamos S/ 1500 como promedio base conservador si no hay dato real
                totalCosto += (1500 / 30) * dias;
            });

            // Calcular KPI: Tasa de Ausentismo
            // (Días perdidos / (Total Empleados * Días Laborables Año)) * 100
            // Asumiendo 260 días laborables al año
            const diasLaborablesTotales = totalEmpleados * 260;
            const tasa = (totalDias / diasLaborablesTotales) * 100;

            // Top Empleados
            const topList = Object.entries(porEmpleado)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([dni, dias]) => ({
                    nombre: empleadosMap[dni] || dni,
                    dias
                }));

            setKpis({
                tasaAusentismo: tasa.toFixed(2),
                diasPerdidos: totalDias,
                costoEstimado: totalCosto,
                empleadosAfectados: Object.keys(porEmpleado).length
            });

            // Configurar gráficos
            setChartData({
                tendencia: {
                    labels: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
                    datasets: [
                        {
                            label: "Días de Ausencia",
                            data: porMes,
                            borderColor: "rgb(53, 162, 235)",
                            backgroundColor: "rgba(53, 162, 235, 0.5)",
                            tension: 0.4,
                            fill: true
                        }
                    ]
                },
                distribucion: {
                    labels: Object.keys(porTipo),
                    datasets: [
                        {
                            data: Object.values(porTipo),
                            backgroundColor: [
                                "#FF6384",
                                "#36A2EB",
                                "#FFCE56",
                                "#4BC0C0",
                                "#9966FF",
                                "#FF9F40"
                            ]
                        }
                    ]
                },
                topEmpleados: topList
            });

        } catch (error) {
            console.error("Error cargando dashboard:", error);
        } finally {
            setLoading(false);
        }
    };

    const optionsLine = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'Tendencia Mensual de Ausentismo' }
        }
    };

    const optionsDoughnut = {
        responsive: true,
        plugins: {
            legend: { position: 'right' },
            title: { display: true, text: 'Distribución por Tipo' }
        }
    };

    return (
        <div
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0"
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                <Card
                    className="shadow-lg border-0"
                    style={{ borderRadius: "15px", overflow: "hidden" }}
                >
                    <Card.Header
                        className="text-white py-4 position-relative"
                        style={{
                            background: "linear-gradient(45deg, #2c3e50, #3498db)",
                            borderBottom: "3px solid #f39c12"
                        }}
                    >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
                        <div className="d-flex justify-content-between align-items-center px-4">
                            <div>
                                <h2 className="fw-bold mb-1">
                                    <FaChartPie className="me-3" />
                                    Dashboard Ejecutivo
                                </h2>
                                <p className="mb-0 opacity-75">
                                    Análisis Estratégico de Ausentismo - {periodo}
                                </p>
                            </div>
                            <Form.Select
                                value={periodo}
                                onChange={(e) => setPeriodo(Number(e.target.value))}
                                style={{ width: "150px" }}
                            >
                                {[2024, 2025, 2026, 2027].map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </Form.Select>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                            </div>
                        ) : (
                            <>
                                {/* Primera fila: KPIs */}
                                <Row className="mb-4 g-4">
                                    <Col md={3}>
                                        <Card className="shadow-sm border-0 h-100">
                                            <Card.Body>
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="bg-primary bg-opacity-10 p-3 rounded-circle me-3">
                                                        <FaChartLine className="text-primary" size={24} />
                                                    </div>
                                                    <h6 className="text-muted mb-0">Tasa de Ausentismo</h6>
                                                </div>
                                                <h2 className="fw-bold mb-0">{kpis.tasaAusentismo}%</h2>
                                                <small className="text-muted">Promedio anual</small>
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    <Col md={3}>
                                        <Card className="shadow-sm border-0 h-100">
                                            <Card.Body>
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="bg-danger bg-opacity-10 p-3 rounded-circle me-3">
                                                        <FaUserInjured className="text-danger" size={24} />
                                                    </div>
                                                    <h6 className="text-muted mb-0">Días Perdidos</h6>
                                                </div>
                                                <h2 className="fw-bold mb-0">{kpis.diasPerdidos}</h2>
                                                <small className="text-muted">Días acumulados</small>
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    <Col md={3}>
                                        <Card className="shadow-sm border-0 h-100">
                                            <Card.Body>
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="bg-success bg-opacity-10 p-3 rounded-circle me-3">
                                                        <FaMoneyBillAlt className="text-success" size={24} />
                                                    </div>
                                                    <h6 className="text-muted mb-0">Costo Estimado</h6>
                                                </div>
                                                <h2 className="fw-bold mb-0">S/ {kpis.costoEstimado.toLocaleString()}</h2>
                                                <small className="text-muted">Proyección directa</small>
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    <Col md={3}>
                                        <Card className="shadow-sm border-0 h-100">
                                            <Card.Body>
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="bg-info bg-opacity-10 p-3 rounded-circle me-3">
                                                        <FaUsers className="text-info" size={24} />
                                                    </div>
                                                    <h6 className="text-muted mb-0">Empleados Afectados</h6>
                                                </div>
                                                <h2 className="fw-bold mb-0">{kpis.empleadosAfectados}</h2>
                                                <small className="text-muted">Con al menos 1 falta</small>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Segunda fila: Gráficos */}
                                <Row className="mb-4 g-4">
                                    <Col md={8}>
                                        <Card className="shadow-sm border-0 h-100">
                                            <Card.Body>
                                                {chartData.tendencia && <Line options={optionsLine} data={chartData.tendencia} />}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={4}>
                                        <Card className="shadow-sm border-0 h-100">
                                            <Card.Body>
                                                {chartData.distribucion && <Doughnut options={optionsDoughnut} data={chartData.distribucion} />}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Tercera fila: Top Empleados */}
                                <Row className="g-4">
                                    <Col md={6}>
                                        <Card className="shadow-sm border-0">
                                            <Card.Header className="bg-white py-3">
                                                <h6 className="fw-bold mb-0">Top 5 - Mayor Ausentismo</h6>
                                            </Card.Header>
                                            <Card.Body className="p-0">
                                                <Table hover className="mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>Empleado</th>
                                                            <th className="text-center">Días</th>
                                                            <th>Estado</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {chartData.topEmpleados.map((emp, i) => (
                                                            <tr key={i}>
                                                                <td>{emp.nombre}</td>
                                                                <td className="text-center fw-bold">{emp.dias}</td>
                                                                <td>
                                                                    {emp.dias > 15 ? (
                                                                        <Badge bg="danger">Crítico</Badge>
                                                                    ) : emp.dias > 5 ? (
                                                                        <Badge bg="warning">Alto</Badge>
                                                                    ) : (
                                                                        <Badge bg="success">Normal</Badge>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {chartData.topEmpleados.length === 0 && (
                                                            <tr>
                                                                <td colSpan={3} className="text-center py-3">No hay datos disponibles</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={6}>
                                        <Card className="shadow-sm border-0 bg-light">
                                            <Card.Body>
                                                <h6 className="fw-bold mb-3">Insights Generados con IA</h6>
                                                <ul className="list-unstyled">
                                                    <li className="mb-2">💡 La tasa de ausentismo está {kpis.tasaAusentismo > 5 ? 'por encima' : 'dentro'} del promedio del sector (5%).</li>
                                                    <li className="mb-2">💡 El mes con mayor incidencia fue {chartData.tendencia &&
                                                        ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Setiembre', 'Octubre', 'Noviembre', 'Diciembre'][
                                                        chartData.tendencia.datasets[0].data.indexOf(Math.max(...chartData.tendencia.datasets[0].data))
                                                        ]
                                                    }.</li>
                                                    <li>💡 Se recomienda revisar los casos críticos de {chartData.topEmpleados[0]?.nombre || "ninguno"}.</li>
                                                </ul>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
}

export default DashboardAusencias;
